import { gql } from "graphql-tag";

/**
 * Retrieves the translations of products, categories and prices
 *
 * Here is a variable example
 * {"pscCond": "(id,in,39701)", "pspCond": "(id,in,5910,5967)", "pCond": "(id,in,36896)"}
 */
export const GET_PCTranslations = gql`
  query GetPCTranslations(
    $pscCond: String!
    $pspCond: String!
    $pCond: String!
  ) {
    categoryTranslations: ProductSlotCategoryTranslationList(where: $pscCond) {
      pcId: ForeignKey
      field: Field
      lang: Locale
      text: Content
    }
    priceTranslations: ProductSlotPriceTranslationList(where: $pspCond) {
      pspId: ForeignKey
      field: Field
      lang: Locale
      text: Content
    }
    productTranslations: ProductTranslationList(where: $pCond) {
      pspId: ForeignKey
      field: Field
      lang: Locale
      text: Content
    }
  }
`;

/** Same as GET_PCList but with more fields and entities to cover Seating Plan logic */
export const GET_PCListWithSeatingPlanData = gql`
  query GETPCListWithSeatingPlanData($categories: String!) {
    productCategory: ProductSlotCategoryList(where: $categories) {
      id: Id
      cancelled: Cancelled
      name: CategoryName
      description: CategoryDescription
      type: Type
      address: Address
      qrCodeType: QrCodeType
      multipleScan: MultipleScan

      saleStartType: SaleStartType
      saleEndType: SaleEndType
      saleStart: SaleStart
      saleEnd: SaleEnd
      salesStartDurationDays: SalesStartDurationDays
      salesEndDurationMinutes: SalesEndDurationMinutes
      sortOrderPos: SortOrderPos

      timeSlotCount: TimeSlotCount

      teamSettingList: TeamSettingList {
        maxParticipants: MaxParticipants
        categoryId: CategoryId
        countedPsp: CountedPsp
      }

      memberships: MembershipList {
        id: Id
        startDate: StartDate
        expiryDate: ExpiryDate
        type: Type
        expiryType: ExpiryType
        periodLength: PeriodLength
        numberMandatoryPeriod: NumberMandatoryPeriod
      }

      promoCodeCampaignList: PromoCodeCampaignList {
        id: Id
        giftCardTicketId: GiftCardTicketId
        startDateTime: StartDateTime
        endDateTime: EndDateTime
        expiryType: ExpiryType
        expiryDays: ExpiryDays
        cancelled: Cancelled
      }

      stockCategoryList: StockCategoryList {
        id: Id
        categoryId: CategoryId
        type: Type
        quantity: Quantity
        holdStock: HoldStock
        stockRead: StockRead {
          id: Id
        }
      }

      ticketGroups: TicketGroupTicketList {
        ticketGroupId: TicketGroupId
      }
      translations: ProductSlotCategoryTranslationList {
        content: Content
        field: Field
        locale: Locale
      }
      product: ProductRead {
        currency: Currency
        name: Name
        id: Id
        isIdivTax: IsIdivTax
        status: Status
        translations: ProductTranslationList {
          content: Content
          field: Field
          locale: Locale
        }
        pictures: ProductPictureList {
          picture: PictureRead {
            name: Name
            isPrimary: IsPrimary
            realUrl: RealUrl
          }
        }
        productBrandingList: ProductBrandingList {
          id: Id
          description1: Description1
          description2: Description2
          description3: Description3
          customText: CustomText
        }
        customTicketFieldsList: CustomTicketFieldList {
          id: Id
          productId: ProductId
          questionType: QuestionType
          name: Name
          list: List
          validation: Validation
          informationType: InformationType
          answerExample: AnswerExample
          position: Position
          canceled: Canceled
          visibleFor: VisibleFor
          prices: CustomTicketFieldPriceList {
            priceId: ProductSlotPriceId
          }
        }
        customBookingFieldsList: CustomBookingFieldList {
          id: Id
          productId: ProductId
          questionType: QuestionType
          name: Name
          list: List
          validation: Validation
          informationType: InformationType
          answerExample: AnswerExample
          position: Position
          canceled: Canceled
          visibleFor: VisibleFor
        }
        GroupRead {
          groupUuid: Uuid
        }
      }
      prices: ProductSlotPriceList {
        id: Id
        name: PriceName
        description: Description
        availability: Availability
        cancelled: Cancelled
        isEditable: IsEditable
        priceCategory: PriceCategory
        sortPrice: SortPrice
        translations: ProductSlotPriceTranslationList {
          content: Content
          field: Field
          locale: Locale
        }
        value: Value
        priceOnsite: PriceOnsite
        quantity: QuantitAvailable
        totalQuantity: Quantity
        vatRateGeneralId: VatRateGeneralId
        tax: TaxRead {
          id: Id
          name: Name
          includedInPrice: IncludedInPrice
          rateMiliValue: RateMiliValue
        }
        addons: PackagePriceList {
          id: Id
          priceId: PriceId
          automaticallyBook: AutomaticallyBook
          canceled: Canceled
          isMandatory: IsMandatory
          priority: Priority
          sendTicket: SendTicket
          displayedValue: DisplayedValue
          accountingValue: AccountingValue
          minutesBefore: MinutesBefore
          minutesAfter: MinutesAfter
          minutesBeforeStartMin: MinutesBeforeStartMin
          minutesBeforeStartMax: MinutesBeforeStartMax
          minutesAfterStartMin: MinutesAfterStartMin
          minutesAfterStartMax: MinutesAfterStartMax
          maxQuantity: MaxQuantity
          minQuantity: MinQuantity
          displayPage: DisplayPage
        }
        updatedAt: UpdatedAt
        retailOptions: RetailOptionList {
          id: Id
          name: OptionName
          nameLocale: OptionNameLocale
          type: OptionType
          inventoryAlert: InventoryAlert
          barcode: Barcode
          sellingSoldOut: SellingSoldOut
          smeetzPictures: SmeetzPictureList {
            id: Id
            metaData: MetaData
            name: Name
            entity: Entity
            entityId: EntityId
          }
          deletedAt: DeletedAt
        }
        packageMinQuantity: PackageMinQuantity
        packageMaxQuantity: PackageMaxQuantity
      }
      resource: ResourceRead {
        quantity: Quantity
      }
      seatingParentPSCId: SeatingMainSubProductId
      seatsCat: SeatsCat
      #seatingMainSubProductRead: SeatingMainSubProductRead {
      #  resourceRead: ResourceRead {
      #    id: Id
      #    name: Name
      #    eventKey: EventKey
      #  }
      #}
    }
  }
`;

/**
 * Pulls parent PSCs from remote using subCategoriesIds.parentPSCId
 * We don't pull all parent PSC fields, we only pull what is needed for Seating Plan logic and to be assigned to their sub-categories
 */
export const GET_ParentPSCs = gql`
  query GETParentPCList($categories: String!) {
    productCategory: ProductSlotCategoryList(where: $categories) {
      id: Id
      cancelled: Cancelled
      name: CategoryName
      description: CategoryDescription
      type: Type
      isRecurringSeat: IsRecurringSeat
      resourceRead: ResourceRead {
        id: Id
        name: Name
        eventKey: EventKey
      }
      ProductRead {
        status: Status
        GroupRead {
          groupUuid: Uuid
        }
      }
    }
  }
`;

/**
 * Pulls parent PSCs from remote with full details
 */
export const GET_ParentPSCsWithDetails = gql`
  query GETParentPCListWithDetails($categories: String!) {
    productCategory: ProductSlotCategoryList(where: $categories) {
      id: Id
      cancelled: Cancelled
      name: CategoryName
      description: CategoryDescription
      type: Type
      address: Address
      qrCodeType: QrCodeType
      multipleScan: MultipleScan
      categoryInfoList: CategoryInfoList {
        scanOpeningMinutesBeforeStart: ScanOpeningMinutesBeforeStart
      }

      isRecurringSeat: IsRecurringSeat
      resourceRead: ResourceRead {
        id: Id
        name: Name
        eventKey: EventKey
        quantity: Quantity
      }

      saleStartType: SaleStartType
      saleEndType: SaleEndType
      saleStart: SaleStart
      saleEnd: SaleEnd
      salesStartDurationDays: SalesStartDurationDays
      salesEndDurationMinutes: SalesEndDurationMinutes
      sortOrderPos: SortOrderPos

      #      teamSettingList: TeamSettingList {
      #        maxParticipants: MaxParticipants
      #        categoryId: CategoryId
      #        countedPsp: CountedPsp
      #      }

      memberships: MembershipList {
        id: Id
        startDate: StartDate
        expiryDate: ExpiryDate
        type: Type
        expiryType: ExpiryType
        periodLength: PeriodLength
        numberMandatoryPeriod: NumberMandatoryPeriod
      }

      promoCodeCampaignList: PromoCodeCampaignList {
        id: Id
        giftCardTicketId: GiftCardTicketId
        startDateTime: StartDateTime
        endDateTime: EndDateTime
        expiryType: ExpiryType
        expiryDays: ExpiryDays
        cancelled: Cancelled
      }

      #      stockCategoryList: StockCategoryList {
      #        id: Id
      #        categoryId: CategoryId
      #        type: Type
      #        quantity: Quantity
      #        holdStock: HoldStock
      #        stockRead: StockRead {
      #          id: Id
      #        }
      #      }

      #      ticketGroups: TicketGroupTicketList {
      #        ticketGroupId: TicketGroupId
      #      }
      translations: ProductSlotCategoryTranslationList {
        content: Content
        field: Field
        locale: Locale
      }
      product: ProductRead {
        currency: Currency
        name: Name
        id: Id
        isIdivTax: IsIdivTax
        status: Status
        translations: ProductTranslationList {
          content: Content
          field: Field
          locale: Locale
        }
        pictures: ProductPictureList {
          picture: PictureRead {
            name: Name
            isPrimary: IsPrimary
            realUrl: RealUrl
          }
        }
        productBrandingList: ProductBrandingList {
          Id
          Description1
          Description2
          Description3
          CustomText
        }
        customTicketFieldsList: CustomTicketFieldList {
          id: Id
          productId: ProductId
          questionType: QuestionType
          name: Name
          list: List
          validation: Validation
          informationType: InformationType
          answerExample: AnswerExample
          position: Position
          canceled: Canceled
          visibleFor: VisibleFor
          prices: CustomTicketFieldPriceList {
            priceId: ProductSlotPriceId
          }
        }
        customBookingFieldsList: CustomBookingFieldList {
          id: Id
          productId: ProductId
          questionType: QuestionType
          name: Name
          list: List
          validation: Validation
          informationType: InformationType
          answerExample: AnswerExample
          position: Position
          canceled: Canceled
          visibleFor: VisibleFor
        }
        GroupRead {
          groupUuid: Uuid
        }
      }
      prices: ProductSlotPriceList {
        id: Id
        name: PriceName
        description: Description
        availability: Availability
        cancelled: Cancelled
        isEditable: IsEditable
        priceCategory: PriceCategory
        sortPrice: SortPrice
        translations: ProductSlotPriceTranslationList {
          content: Content
          field: Field
          locale: Locale
        }
        value: Value
        priceOnsite: PriceOnsite
        quantity: QuantitAvailable
        totalQuantity: Quantity
        vatRateGeneralId: VatRateGeneralId
        tax: TaxRead {
          id: Id
          name: Name
          includedInPrice: IncludedInPrice
          rateMiliValue: RateMiliValue
        }
        addons: PackagePriceList {
          id: Id
          priceId: PriceId
          automaticallyBook: AutomaticallyBook
          canceled: Canceled
          isMandatory: IsMandatory
          priority: Priority
          sendTicket: SendTicket
          displayedValue: DisplayedValue
          accountingValue: AccountingValue
          minutesBefore: MinutesBefore
          minutesAfter: MinutesAfter
          minutesBeforeStartMin: MinutesBeforeStartMin
          minutesBeforeStartMax: MinutesBeforeStartMax
          minutesAfterStartMin: MinutesAfterStartMin
          minutesAfterStartMax: MinutesAfterStartMax
          maxQuantity: MaxQuantity
          minQuantity: MinQuantity
          displayPage: DisplayPage
        }
        updatedAt: UpdatedAt
        #        retailOptions: RetailOptionList {
        #          id: Id
        #          name: OptionName
        #          nameLocale: OptionNameLocale
        #          type: OptionType
        #          inventoryAlert: InventoryAlert
        #          barcode: Barcode
        #          sellingSoldOut: SellingSoldOut
        #          smeetzPictures: SmeetzPictureList {
        #            id: Id
        #            metaData: MetaData
        #            name: Name
        #            entity: Entity
        #            entityId: EntityId
        #          }
        #        }
      }
    }
  }
`;

export const GET_HandshakeCategoryTspropertyList = gql`
  query GETHandshakeCategoryTspropertyList($condition: String!) {
    HandshakeCategoryTspropertyList: HandshakeCategoryTspropertyList(
      where: $condition
    ) {
      id: Id
      categoryId: CategoryId
      areaName: AreaName
      areaCode: AreaCode
      ticketTypeName: TicketTypeName
      ticketTypeCode: TicketTypeCode
      ProductSlotCategoryRead {
        ProductRead {
          GroupRead {
            groupUuid: Uuid
          }
        }
      }
    }
  }
`;
